import React, { useState } from 'react';
import { 
  Button, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  TextField, 
  Grid, 
  Typography, 
  Snackbar, 
  Alert,
  CircularProgress,
  IconButton
} from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import CloseIcon from '@mui/icons-material/Close';
import api from '../api';
import logger from './../utils/logger.js';

const AddRecordButton = ({ onRecordAdded }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });
  const [formData, setFormData] = useState({
    Instellingsnaam: '',
    'Vestigingsadres Plaats': '',
    URL: '',
    Telefoonnummer: '',
    'LinkedIn Link': '',
    'Sector 2': '',
    Email: ''
  });

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    // Reset form when dialog is closed
    setFormData({
      Instellingsnaam: '',
      'Vestigingsadres Plaats': '',
      URL: '',
      Telefoonnummer: '',
      'LinkedIn Link': '',
      'Sector 2': '',
      Email: ''
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const showNotification = (message, severity = 'success') => {
    setNotification({
      open: true,
      message,
      severity
    });
  };

  const handleCloseNotification = () => {
    setNotification({
      ...notification,
      open: false
    });
  };

  const handleSubmit = async () => {
    // Validate required fields
    const requiredFields = ['Instellingsnaam', 'Vestigingsadres Plaats', 'URL', 'Telefoonnummer'];
    const missingFields = requiredFields.filter(field => !formData[field]);
    
    if (missingFields.length > 0) {
      showNotification(`Please fill in all required fields: ${missingFields.join(', ')}`, 'error');
      return;
    }

    setLoading(true);
    try {
      const response = await api.addRecord(formData);
      
      if (response.success) {
        showNotification(`Record ${response.is_new ? 'added' : 'updated'} successfully`);
        handleClose();
        
        // Trigger parent component refresh if callback provided
        if (onRecordAdded && typeof onRecordAdded === 'function') {
          onRecordAdded(response.record);
        }
      } else {
        showNotification(response.message || 'Failed to add record', 'error');
      }
    } catch (error) {
      logger.error('Error adding record:', error);
      const errorMessage = error.response?.data?.message || 'An unexpected error occurred';
      showNotification(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Button
        variant="contained"
        color="primary"
        startIcon={<AddCircleOutlineIcon />}
        onClick={handleOpen}
        sx={{ mb: 2 }}
      >
        Add Record
      </Button>

      <Dialog 
        open={open} 
        onClose={handleClose} 
        maxWidth="md" 
        fullWidth
        aria-labelledby="add-record-dialog-title"
      >
        <DialogTitle id="add-record-dialog-title">
          Add New Record
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        
        <DialogContent dividers>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Fields marked with * are required
          </Typography>
          
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                name="Instellingsnaam"
                label="Institution Name *"
                value={formData.Instellingsnaam}
                onChange={handleChange}
                fullWidth
                margin="normal"
                required
                error={!formData.Instellingsnaam}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                name="Vestigingsadres Plaats"
                label="City *"
                value={formData['Vestigingsadres Plaats']}
                onChange={handleChange}
                fullWidth
                margin="normal"
                required
                error={!formData['Vestigingsadres Plaats']}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                name="URL"
                label="Website URL *"
                value={formData.URL}
                onChange={handleChange}
                fullWidth
                margin="normal"
                required
                error={!formData.URL}
                placeholder="https://example.com"
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                name="Telefoonnummer"
                label="Phone Number *"
                value={formData.Telefoonnummer}
                onChange={handleChange}
                fullWidth
                margin="normal"
                required
                error={!formData.Telefoonnummer}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                name="LinkedIn Link"
                label="LinkedIn URL"
                value={formData['LinkedIn Link']}
                onChange={handleChange}
                fullWidth
                margin="normal"
                placeholder="https://linkedin.com/company/example"
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                name="Sector 2"
                label="Sector"
                value={formData['Sector 2']}
                onChange={handleChange}
                fullWidth
                margin="normal"
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                name="Email"
                label="Email"
                value={formData.Email}
                onChange={handleChange}
                fullWidth
                margin="normal"
                type="email"
              />
            </Grid>
          </Grid>
        </DialogContent>
        
        <DialogActions>
          <Button onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit} 
            color="primary" 
            variant="contained" 
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : null}
          >
            {loading ? 'Saving...' : 'Save Record'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar 
        open={notification.open} 
        autoHideDuration={6000} 
        onClose={handleCloseNotification}
      >
        <Alert 
          onClose={handleCloseNotification} 
          severity={notification.severity} 
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default AddRecordButton; 